// src/features/merchant/dashboardSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchDashboard,
  updateOverview,
  createCatalogueItem,
  updateCatalogueItem,
  deleteCatalogueItem,
  updateQuickInfoText,
  uploadQuickInfoImages,
  deleteQuickInfoImage,
  fetchKeywords,
  createKeyword,
  updateMerchantKeywords,
} from './dashboardThunks';

const initialState = {
  data: null,
  loading: false,
  updating: false,
  error: null,
  // keywords search list (from master)
  allKeywords: [],
  keywordsLoading: false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboard(state) {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== DASHBOARD FETCH =====
      .addCase(fetchDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data || null;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // ===== OVERVIEW UPDATE =====
      .addCase(updateOverview.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateOverview.fulfilled, (state, action) => {
        state.updating = false;
        const returned = action.payload?.data || action.payload;
        if (returned && state.data) {
          state.data.overview = returned.overview || returned;
        }
      })
      .addCase(updateOverview.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error?.message;
      })

      // ===== CATALOGUE CREATE / UPDATE / DELETE =====
      .addCase(createCatalogueItem.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(createCatalogueItem.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(createCatalogueItem.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error?.message;
      })

      .addCase(updateCatalogueItem.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateCatalogueItem.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateCatalogueItem.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error?.message;
      })

      .addCase(deleteCatalogueItem.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(deleteCatalogueItem.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(deleteCatalogueItem.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error?.message;
      })

      // ===== QUICK INFO =====
      .addCase(updateQuickInfoText.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateQuickInfoText.fulfilled, (state, action) => {
        state.updating = false;
        if (state.data) {
          state.data.quickInfo = action.payload?.data?.quickInfo;
        }
      })
      .addCase(updateQuickInfoText.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })

      .addCase(uploadQuickInfoImages.pending, (state) => {
        state.updating = true;
      })
      .addCase(uploadQuickInfoImages.fulfilled, (state, action) => {
        state.updating = false;
        if (state.data) {
          state.data.quickInfo = action.payload?.data?.quickInfo;
        }
      })
      .addCase(uploadQuickInfoImages.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })

      .addCase(deleteQuickInfoImage.pending, (state) => {
        state.updating = true;
      })
      .addCase(deleteQuickInfoImage.fulfilled, (state, action) => {
        state.updating = false;
        if (state.data) {
          state.data.quickInfo = action.payload.res.data.quickInfo;
        }
      })
      .addCase(deleteQuickInfoImage.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })

      // ===== BUSINESS KEYWORDS =====
      .addCase(fetchKeywords.pending, (state) => {
        state.keywordsLoading = true;
      })
      .addCase(fetchKeywords.fulfilled, (state, action) => {
        state.keywordsLoading = false;
        state.allKeywords = action.payload?.data || [];
      })
      .addCase(fetchKeywords.rejected, (state) => {
        state.keywordsLoading = false;
      })

      .addCase(createKeyword.fulfilled, (state, action) => {
        const kw = action.payload?.data;
        if (kw && !state.allKeywords.find(k => k._id === kw._id)) {
          state.allKeywords.push(kw);
        }
      })

      .addCase(updateMerchantKeywords.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateMerchantKeywords.fulfilled, (state, action) => {
        state.updating = false;
        if (state.data) {
          state.data.businessKeywords = action.payload?.data?.businessKeywords || [];
        }
      })
      .addCase(updateMerchantKeywords.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      });
  },
});

export const { clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
